// Editor for the small data lists: budgetBuckets, campaignBuckets, and the
// combined OGS Hubs & Functions matrix (each hub picks which functions it
// covers — that coverage feeds the intake's OGS chips).

import clsx from 'clsx';
import { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, X } from 'lucide-react';
import { modelCurrency } from '@rc/engine';
import type { BudgetBucket, CampaignBucket, Model, OgsFunction, OgsHub } from '@rc/engine/types';

interface Props {
  model: Model;
  onChange: (m: Model) => void;
}

export default function BucketsHubsEditor({ model, onChange }: Props) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BudgetBucketsList model={model} onChange={onChange} />
        <CampaignBucketsList model={model} onChange={onChange} />
      </div>
      <OgsHubsAndFunctions model={model} onChange={onChange} />
    </div>
  );
}

function Section({
  title,
  description,
  children,
  onAdd,
  right,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  onAdd?: () => void;
  right?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200">
      <div className="px-4 py-3 border-b border-slate-100 flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-800">{title}</h4>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
        {right}
      </div>
      <div className="p-3 space-y-2">{children}</div>
      {onAdd && (
        <div className="px-3 pb-3">
          <button
            type="button"
            onClick={onAdd}
            className="text-xs text-brand-purple-700 hover:text-brand-purple-900 inline-flex items-center gap-1"
          >
            <Plus size={12} /> Add row
          </button>
        </div>
      )}
    </div>
  );
}

function BudgetBucketsList({ model, onChange }: Props) {
  const rows = model.budgetBuckets || [];
  const base = modelCurrency(model).base;
  const update = (next: BudgetBucket[]) => onChange({ ...model, budgetBuckets: next });
  const set = (i: number, patch: Partial<BudgetBucket>) => {
    const next = [...rows];
    next[i] = { ...next[i], ...patch };
    update(next);
  };
  const remove = (i: number) => update(rows.filter((_, j) => j !== i));
  const add = () =>
    update([...rows, { id: `bucket-${rows.length + 1}`, label: 'New bucket', minUsd: 0, maxUsd: null }]);

  return (
    <Section
      title="Budget buckets"
      description={`Auto-derives the 'budget_bucket' dimension value from a scenario's totalBudget. Min and max are in ${base}.`}
      onAdd={add}
    >
      {rows.length > 0 && (
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-400">
          <span className="flex-1">Label</span>
          <span className="w-28 text-right">Min ({base})</span>
          <span className="w-28 text-right">Max ({base})</span>
          <span className="w-[14px]" />
        </div>
      )}
      {rows.map((b, i) => (
        <div key={b.id} className="flex items-center gap-2 text-sm">
          <input
            className="input py-1 px-2 flex-1"
            value={b.label}
            onChange={(e) => set(i, { label: e.target.value })}
          />
          <input
            type="number"
            className="input py-1 px-2 w-28 text-right"
            value={b.minUsd}
            onChange={(e) => set(i, { minUsd: Number(e.target.value) })}
          />
          <input
            type="number"
            className="input py-1 px-2 w-28 text-right"
            value={b.maxUsd ?? ''}
            placeholder="(open)"
            onChange={(e) =>
              set(i, { maxUsd: e.target.value === '' ? null : Number(e.target.value) })
            }
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className="text-slate-400 hover:text-rose-500"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </Section>
  );
}

function CampaignBucketsList({ model, onChange }: Props) {
  const rows = model.campaignBuckets || [];
  const update = (next: CampaignBucket[]) => onChange({ ...model, campaignBuckets: next });
  const set = (i: number, patch: Partial<CampaignBucket>) => {
    const next = [...rows];
    next[i] = { ...next[i], ...patch };
    update(next);
  };
  const remove = (i: number) => update(rows.filter((_, j) => j !== i));
  const add = () =>
    update([...rows, { id: `cb-${rows.length + 1}`, label: 'New range', min: 0, max: null }]);

  return (
    <Section
      title="Campaign buckets"
      description="Volume buckets for '# campaigns per year'."
      onAdd={add}
    >
      {rows.map((b, i) => (
        <div key={b.id} className="flex items-center gap-2 text-sm">
          <input
            className="input py-1 px-2 flex-1"
            value={b.label}
            onChange={(e) => set(i, { label: e.target.value })}
          />
          <input
            type="number"
            className="input py-1 px-2 w-20 text-right"
            value={b.min}
            onChange={(e) => set(i, { min: Number(e.target.value) })}
          />
          <input
            type="number"
            className="input py-1 px-2 w-20 text-right"
            value={b.max ?? ''}
            placeholder="(open)"
            onChange={(e) =>
              set(i, { max: e.target.value === '' ? null : Number(e.target.value) })
            }
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className="text-slate-400 hover:text-rose-500"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Combined OGS Hubs & Functions
// Each hub row has a chip multi-select of supported functions. Selecting a
// hub in the intake will filter the function chips to exactly this list.
// The function catalog lives in a sidebar so admins can rename / add / remove
// functions; coverage updates ripple into the chips below automatically.
// ---------------------------------------------------------------------------

function OgsHubsAndFunctions({ model, onChange }: Props) {
  const hubs = model.ogsHubs || [];
  const fns = model.ogsFunctions || [];

  const updateHubs = (next: OgsHub[]) => onChange({ ...model, ogsHubs: next });
  const setHub = (i: number, patch: Partial<OgsHub>) => {
    const next = [...hubs];
    next[i] = { ...next[i], ...patch };
    updateHubs(next);
  };
  const removeHub = (i: number) => updateHubs(hubs.filter((_, j) => j !== i));
  const addHub = () =>
    updateHubs([
      ...hubs,
      {
        id: `hub-${hubs.length + 1}`,
        label: 'New hub',
        shore: 'offshore',
        functionIds: fns.map((f) => f.id),
      },
    ]);

  const updateFns = (next: OgsFunction[]) => onChange({ ...model, ogsFunctions: next });
  const setFn = (i: number, patch: Partial<OgsFunction>) => {
    const next = [...fns];
    next[i] = { ...next[i], ...patch };
    updateFns(next);
  };
  const removeFn = (i: number) => {
    const removed = fns[i];
    const nextFns = fns.filter((_, j) => j !== i);
    if (!removed) {
      updateFns(nextFns);
      return;
    }
    // Apply both edits in a SINGLE onChange so neither overwrites the other.
    // (Calling updateFns followed by updateHubs would both build their next
    // model off the same stale prop and clobber each other.)
    const nextHubs = hubs.map((h) => ({
      ...h,
      functionIds: (h.functionIds || []).filter((id) => id !== removed.id),
    }));
    onChange({ ...model, ogsFunctions: nextFns, ogsHubs: nextHubs });
  };
  const addFn = () =>
    updateFns([...fns, { id: `fn-${fns.length + 1}`, label: 'New function' }]);

  return (
    <div className="rounded-lg border border-slate-200">
      <div className="px-4 py-3 border-b border-slate-100">
        <h4 className="text-sm font-semibold text-slate-800">OGS hubs & functions</h4>
        <p className="text-xs text-slate-500">
          For each hub, choose which OGS functions it covers. The intake's OGS card will only
          let users select the functions supported by the chosen hub.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="p-3 space-y-2 border-b lg:border-b-0 lg:border-r border-slate-100">
          {hubs.length === 0 && (
            <div className="text-xs text-slate-400 italic px-1 py-3">
              No hubs yet. Add one to start mapping function coverage.
            </div>
          )}
          {hubs.map((h, i) => (
            <HubRow
              key={h.id}
              hub={h}
              functions={fns}
              onChange={(patch) => setHub(i, patch)}
              onRemove={() => removeHub(i)}
            />
          ))}
          <button
            type="button"
            onClick={addHub}
            className="text-xs text-brand-purple-700 hover:text-brand-purple-900 inline-flex items-center gap-1 mt-2"
          >
            <Plus size={12} /> Add hub
          </button>
        </div>

        <div className="p-3 bg-slate-50/50">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Function catalog
          </div>
          <p className="text-[11px] text-slate-500 mb-2 leading-relaxed">
            Master list of OGS functions. Toggle them on/off per hub in the rows on the left.
          </p>
          <div className="space-y-1.5">
            {fns.map((f, i) => (
              <div key={f.id} className="flex items-center gap-2 text-sm">
                <input
                  className="input py-1 px-2 flex-1"
                  value={f.label}
                  onChange={(e) => setFn(i, { label: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => removeFn(i)}
                  className="text-slate-400 hover:text-rose-500"
                  title="Remove function from catalog"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addFn}
            className="text-xs text-brand-purple-700 hover:text-brand-purple-900 inline-flex items-center gap-1 mt-3"
          >
            <Plus size={12} /> Add function
          </button>
        </div>
      </div>
    </div>
  );
}

function HubRow({
  hub,
  functions,
  onChange,
  onRemove,
}: {
  hub: OgsHub;
  functions: OgsFunction[];
  onChange: (patch: Partial<OgsHub>) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(true);
  const covered = new Set(hub.functionIds || []);
  const noneSelected = (hub.functionIds || []).length === 0;
  const toggleFn = (id: string) => {
    const cur = hub.functionIds || [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    onChange({ functionIds: next });
  };
  const selectAll = () => onChange({ functionIds: functions.map((f) => f.id) });
  const clearAll = () => onChange({ functionIds: [] });

  return (
    <div className="rounded-md border border-slate-200 bg-white">
      <div className="px-3 py-2 flex items-center gap-2 text-sm">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-slate-400 hover:text-slate-700 shrink-0"
          title={open ? 'Collapse' : 'Expand'}
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <input
          className="input py-1 px-2 flex-1"
          value={hub.label}
          onChange={(e) => onChange({ label: e.target.value })}
        />
        <select
          className="input py-1 px-2 w-32"
          value={hub.shore}
          onChange={(e) => onChange({ shore: e.target.value as OgsHub['shore'] })}
        >
          <option value="nearshore">Nearshore</option>
          <option value="offshore">Offshore</option>
        </select>
        <span
          className={clsx(
            'text-[11px] px-2 py-0.5 rounded-md tabular-nums shrink-0',
            noneSelected
              ? 'bg-amber-100 text-amber-700'
              : 'bg-slate-100 text-slate-600',
          )}
          title="Number of functions this hub covers"
        >
          {noneSelected ? 'no functions' : `${covered.size} fns`}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="text-slate-400 hover:text-rose-500 shrink-0"
          title="Remove hub"
        >
          <X size={14} />
        </button>
      </div>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Functions covered
            </span>
            <div className="flex items-center gap-2 text-[11px]">
              <button
                type="button"
                onClick={selectAll}
                className="text-brand-purple-700 hover:text-brand-purple-900"
              >
                Select all
              </button>
              <span className="text-slate-300">·</span>
              <button
                type="button"
                onClick={clearAll}
                className="text-slate-500 hover:text-slate-700"
              >
                Clear
              </button>
            </div>
          </div>
          {functions.length === 0 ? (
            <p className="text-[11px] text-slate-400 italic">
              No functions in the catalog yet — add one on the right.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {functions.map((f) => {
                const on = covered.has(f.id);
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => toggleFn(f.id)}
                    className={clsx(
                      'pill border transition-colors text-xs',
                      on
                        ? 'bg-brand-purple-100 text-brand-purple-700 border-brand-purple-300'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
                    )}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
